import React, { useState, useEffect } from "react";
import "./css/TextMessageAnalysis.css";
import "../../css/Common.css";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { useTheme } from "../../context/ThemeContext";
import { useLoading } from "../../../../contexts/LoadingContext";
import ScamDetectorAPI from "../../services/ScamDetectorAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import PhoneNumberScamCheckAPI from "../../services/PhoneNumberScamCheckAPI";
import DisclosuresModal from "../common/DisclosuresModal";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";
import TextMessageAnalysisMobile from "./TextMessageAnalysisMobile";
import TextMessageAnalysisDesktop from "./TextMessageAnalysisDesktop";
import {
  getOverallRiskLevel,
  getOverallRiskType,
  getOverallRecommendation,
  isHighRisk,
  isMediumRisk,
} from "./textAnalysisUtils";

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

function TextMessageAnalysis() {
  // Mobile detection constant
  const isMobile = () => window.innerWidth <= 768;
  const [showMobileView, setShowMobileView] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => setShowMobileView(isMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Theme hook
  const { effectiveTheme } = useTheme();

  // Loading hook for full-screen modal
  const { isLoading, showLoading, hideLoading } = useLoading();

  const { t } = useTranslation();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [textMessage, setTextMessage] = useState("");
  const [userContext, setUserContext] = useState("");
  const [analysisResults, setAnalysisResults] = useState(null);
  const [error, setError] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [hasUserInput, setHasUserInput] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState({
    isValid: true,
    isComplete: false,
  });
  const [phoneInputKey, setPhoneInputKey] = useState(0);
  // Reusable validation functions
  const shouldShowPhoneValidationError = () => {
    return (
      phoneNumber &&
      phoneNumber.length > 2 &&
      phoneValidation.isComplete &&
      !phoneValidation.isValid
    );
  };

  const handlePhoneChange = (e164Value, validationInfo) => {
    setPhoneNumber(e164Value);

    if (validationInfo) {
      setPhoneValidation({
        isValid: validationInfo.isValid,
        isComplete: validationInfo.isComplete,
      });
    }

    // Update hasUserInput with current values
    setHasUserInput(
      (e164Value && e164Value.length > 2) || textMessage.trim().length > 0,
    );
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setTextMessage(value);

    // Update hasUserInput with current values
    setHasUserInput(
      (phoneNumber && phoneNumber.length > 2) || value.trim().length > 0,
    );
  };

  const handleClearAnalysis = () => {
    setPhoneNumber("");
    setTextMessage("");
    setUserContext("");
    setHasUserInput(false);
    setAnalysisResults(null);
    setError(null);
    setPhoneInputKey((k) => k + 1);
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    // Must have text message content
    if (!textMessage.trim()) return false;

    // Must have a valid phone number
    const hasValidPhone =
      phoneNumber && phoneNumber.length > 2 && phoneValidation.isValid;

    return hasValidPhone;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim() && !textMessage.trim()) {
      setError(t("textAnalysis.error.emptyInput"));
      return;
    }

    showLoading(
      t("textAnalysis.loading.title"),
      t("textAnalysis.loading.subtitle"),
    );
    setError(null);

    try {
      const senderIdentifier = phoneNumber.trim() || null;

      // Perform combined analysis using the unified endpoint
      const combinedResults = await ScamDetectorAPI.analyzeText(
        senderIdentifier,
        textMessage.trim() || null,
        userContext.trim() || null,
      );

      // If a phone number is provided, also run the
      // dedicated phone number scam check for richer database lookup results
      if (phoneNumber.trim()) {
        try {
          const digits = phoneNumber.replace(/\D/g, "");
          const phoneScamResponse =
            await PhoneNumberScamCheckAPI.analyzePhoneNumber(digits);
          const phoneScamData = phoneScamResponse.data || phoneScamResponse;

          // Replace the basic phone analysis with the richer scam check results
          combinedResults.phoneAnalysis = phoneScamData;

          console.log(
            "[TextMessageAnalysis] Phone scam check response:",
            JSON.stringify(phoneScamData, null, 2),
          );
        } catch (phoneErr) {
          console.warn(
            "[TextMessageAnalysis] Phone scam check failed, using basic analysis:",
            phoneErr,
          );
          // Keep the basic phone analysis from the combined results
        }
      }

      console.log(
        "[TextMessageAnalysis] Full API Response:",
        JSON.stringify(combinedResults, null, 2),
      );

      // Show initial combined results quickly. URL follow-ups are now performed
      // by the server-side AI analysis (included in the combinedResults when available).
      setAnalysisResults(combinedResults);

      // Save scam report immediately when scam detected
      const riskLevel = getOverallRiskLevel(combinedResults);
      if (isHighRisk(riskLevel) || isMediumRisk(riskLevel)) {
        try {
          const saveResponse = await ScamReportAPI.saveReport({
            analysisType: "TEXT_ANALYSIS",
            analysisTitle: "Text Message Scam Detected",
            analysisDetail: `Phone: ${phoneNumber}`,
            analysisInput: textMessage,
            context: userContext?.trim() || undefined,
            isScam: combinedResults?.aiAnalysis?.isScam ?? true,
            confidence: combinedResults?.aiAnalysis?.confidence,
            riskLevel: riskLevel,
            explanation: combinedResults?.aiAnalysis?.explanation,
            suggestions: combinedResults?.aiAnalysis?.suggestions,
          });
          if (saveResponse?.reportId) setReportId(saveResponse.reportId);
        } catch (saveErr) {
          console.error("Failed to save scam report:", saveErr);
        }
      }
    } catch (err) {
      console.error("[TextMessageAnalysis] Analysis failed:", err);

      // Check if it's a network error vs server error
      if (err.code === "TIMEOUT") {
        setError(t("textAnalysis.error.timeout"));
      } else if (err.response) {
        // Server responded with an error
        const status = err.response.status;
        const statusText = err.response.statusText || "Unknown error";
        setError(
          t("textAnalysis.error.server")
            .replace("{status}", status)
            .replace("{text}", statusText),
        );
      } else if (err.message && err.message.includes("Failed to fetch")) {
        // Network connectivity issue
        setError(t("textAnalysis.error.network"));
      } else {
        // Generic error
        setError(t("textAnalysis.error.generic"));
      }
    } finally {
      hideLoading();
    }
  };

  // Wrapper functions that pass component state to imported utils
  // (passed as props to TextMessageAnalysisMobile)
  const computeOverallRiskLevel = () => getOverallRiskLevel(analysisResults);
  const computeOverallRiskType = () => getOverallRiskType(analysisResults);
  const computeOverallRecommendation = () =>
    getOverallRecommendation(analysisResults);

  return (
    <div
      className={`common-container text-message-analysis-container${showMobileView ? " tmsm-mobile-active" : ""}`}
      data-theme={effectiveTheme}
    >
      <ReadOnlyBanner />
      {showMobileView ? (
        <TextMessageAnalysisMobile
          phoneNumber={phoneNumber}
          textMessage={textMessage}
          analysisResults={analysisResults}
          error={error}
          isLoading={isLoading}
          hasUserInput={hasUserInput}
          phoneValidation={phoneValidation}
          reportId={reportId}
          handlePhoneChange={handlePhoneChange}
          handleTextChange={handleTextChange}
          handleClearAnalysis={handleClearAnalysis}
          handleSubmit={handleSubmit}
          isFormValid={isFormValid}
          shouldShowPhoneValidationError={shouldShowPhoneValidationError}
          getOverallRiskLevel={computeOverallRiskLevel}
          getOverallRiskType={computeOverallRiskType}
          getOverallRecommendation={computeOverallRecommendation}
          userContext={userContext}
          setUserContext={setUserContext}
          phoneInputKey={phoneInputKey}
        />
      ) : (
        <TextMessageAnalysisDesktop
          phoneNumber={phoneNumber}
          textMessage={textMessage}
          userContext={userContext}
          setUserContext={setUserContext}
          analysisResults={analysisResults}
          error={error}
          isLoading={isLoading}
          hasUserInput={hasUserInput}
          phoneValidation={phoneValidation}
          reportId={reportId}
          handlePhoneChange={handlePhoneChange}
          handleTextChange={handleTextChange}
          handleClearAnalysis={handleClearAnalysis}
          handleSubmit={handleSubmit}
          isFormValid={isFormValid}
          shouldShowPhoneValidationError={shouldShowPhoneValidationError}
          phoneInputKey={phoneInputKey}
        />
      )}

      <DisclosuresModal disclaimerText="The text message analysis provided is for educational purposes only. For complete terms and limitations, please review our" />
    </div>
  );
}

export default TextMessageAnalysis;
